import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, PageTitle, StatusBadge, fmtMoney, fmtDateTime, Th, Td, Modal, STATUS_LABELS, inputCls, labelCls, btnPrimary, btnGhost } from "@/admin/ui";

const TABS = ["all", "new", "in_review", "in_production", "shipped", "delivered", "cancelled"];

const GENDER_LABELS = { men: "Men's", women: "Women's", unisex: "Unisex" };

function tapstitchSummary(o) {
  const a = o.shipping_address || {};
  const items = (o.items || []).map((i) =>
    `- ${i.name} | Gender: ${GENDER_LABELS[i.gender] || "-"} | Size: ${i.size || "-"} | Color: ${i.color || "-"} | Qty: ${i.quantity}`).join("\n");
  return `SYNC ORDER ${o.order_number || o.session_id}
Customer: ${o.customer_name || "-"}
Email: ${o.customer_email || "-"}
Phone: ${o.customer_phone || "-"}
Ship to: ${[a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ")}
Items:
${items}
Order total: ${fmtMoney(o.amount_total ?? o.amount)}
Design: SYNC chest logo (per published Tapstitch product)`;
}

function TapstitchPanel({ order }) {
  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(tapstitchSummary(order));
      toast.success("Fulfillment summary copied — paste it into Tapstitch.");
    } catch { toast.error("Could not copy."); }
  };

  return (
    <div>
      <p className={labelCls}>Tapstitch fulfillment</p>
      <p className="text-graphite text-xs leading-relaxed mb-3">
        Tapstitch has no public API — orders are placed manually in your Tapstitch dashboard.
        Copy the summary below and paste it there, then mark this order In Production.
      </p>
      <div className="flex flex-wrap gap-3">
        <button onClick={copySummary} className={btnGhost} data-testid="order-copy-tapstitch">Copy Fulfillment Summary</button>
        <a href="https://www.tapstitch.com" target="_blank" rel="noopener noreferrer"
          className={`${btnGhost} inline-flex items-center`} data-testid="order-open-tapstitch">Open Tapstitch ↗</a>
      </div>
      {order.sent_to_fulfillment_at && (
        <p className="font-mono text-[10px] text-signal mt-3">Sent to production {fmtDateTime(order.sent_to_fulfillment_at)}</p>
      )}
    </div>
  );
}

function OrderDetail({ order, onClose, onUpdated }) {
  const KNOWN_CARRIERS = ["USPS", "UPS", "FedEx", "DHL"];
  const initialKnown = !order.carrier || KNOWN_CARRIERS.includes(order.carrier);
  const [status, setStatus] = useState(order.fulfillment_status || "new");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(initialKnown ? (order.carrier || "") : "Other");
  const [customCarrier, setCustomCarrier] = useState(initialKnown ? "" : order.carrier);
  const [saving, setSaving] = useState(false);
  const a = order.shipping_address || {};

  const save = async () => {
    const effectiveCarrier = carrier === "Other" ? customCarrier.trim() : carrier;
    if (carrier === "Other" && !effectiveCarrier) {
      toast.error("Enter the carrier name.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.session_id}/status`, { status, tracking_number: tracking || null, carrier: effectiveCarrier || null });
      toast.success(`Order marked ${STATUS_LABELS[status]}.`);
      onUpdated();
    } catch { toast.error("Could not update order."); }
    setSaving(false);
  };

  return (
    <Modal open onClose={onClose} wide>
      <div data-testid="order-detail">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <h2 className="font-anton uppercase text-2xl">{order.order_number || `Order …${order.session_id.slice(-8)}`}</h2>
          <StatusBadge status={order.fulfillment_status || "new"} />
          <span className="font-mono text-[10px] text-graphite">{fmtDateTime(order.created_at)}</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <p className={labelCls}>Customer</p>
            <p className="text-sm">{order.customer_name || "—"}</p>
            <p className="font-mono text-xs text-graphite">{order.customer_email || "—"}</p>
            {order.customer_phone && (
              <p className="font-mono text-xs text-graphite" data-testid="order-customer-phone">{order.customer_phone}</p>
            )}
            <p className={`${labelCls} mt-4`}>Shipping address</p>
            {order.shipping_address ? (
              <p className="text-sm leading-relaxed" data-testid="order-shipping">
                {a.line1}{a.line2 ? <>, {a.line2}</> : null}<br />
                {[a.city, a.state, a.postal_code].filter(Boolean).join(", ")}<br />{a.country}
              </p>
            ) : <p className="text-graphite text-sm">Not captured (older order)</p>}
          </div>
          <div>
            <p className={labelCls}>Items</p>
            <div className="space-y-2">
              {(order.items || []).map((i) => (
                <div key={`${i.name}-${i.size}-${i.color}`} className="flex justify-between border-b border-border/50 pb-2 text-sm">
                  <span>
                    {i.quantity}× {i.name}{" "}
                    <span className="font-mono text-[10px] text-graphite">{[i.size, i.color].filter(Boolean).join(" / ")}</span>
                    {i.gender && (
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] border border-border text-chalk/60 px-1.5 py-0.5"
                        data-testid="order-item-gender">{GENDER_LABELS[i.gender] || i.gender}</span>
                    )}
                  </span>
                  <span className="font-mono text-xs">{fmtMoney(i.price * i.quantity)}</span>
                </div>
              ))}
              {order.tax_amount > 0 && (
                <div className="flex justify-between pt-1" data-testid="order-tax-row">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-graphite">Sales tax</span>
                  <span className="font-mono text-xs text-graphite">{fmtMoney(order.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-1">
                <span className="font-mono text-[11px] uppercase tracking-wider text-graphite">Total charged</span>
                <span className="font-mono text-sm text-signal">{fmtMoney(order.amount_total ?? order.amount)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border mt-6 pt-6 grid sm:grid-cols-2 gap-6">
          <div>
            <p className={labelCls}>Update status</p>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-status-select"
              className={`${inputCls} bg-ink`}>
              {TABS.slice(1).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
            <p className={`${labelCls} mt-3`}>Shipping carrier</p>
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="order-carrier-select"
              className={`${inputCls} bg-ink`}>
              <option value="">— Select carrier —</option>
              {["USPS", "UPS", "FedEx", "DHL", "Other"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {carrier === "Other" && (
              <>
                <p className={`${labelCls} mt-3`}>Carrier name</p>
                <input value={customCarrier} onChange={(e) => setCustomCarrier(e.target.value)} className={inputCls}
                  placeholder="e.g. OnTrac, Canada Post…" maxLength={40} data-testid="order-carrier-custom-input" />
              </>
            )}
            <p className={`${labelCls} mt-3`}>Tracking number (optional)</p>
            <input value={tracking} onChange={(e) => setTracking(e.target.value)} className={inputCls}
              placeholder="e.g. 1Z999AA10123456784" data-testid="order-tracking-input" />
            <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="order-status-save">
              {saving ? "Saving…" : "Update Order"}
            </button>
            <p className="font-mono text-[10px] text-graphite mt-2">Marking "Shipped" with a carrier + tracking number emails the customer automatically with a tracking link.</p>
          </div>
          <TapstitchPanel order={order} />
        </div>
      </div>
    </Modal>
  );
}

function ManualOrderForm({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ customer_name: "", customer_phone: "", customer_email: "", order_type: "gift",
    line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const [items, setItems] = useState([{ product_id: "", color: "", size: "", quantity: 1 }]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const prodOf = (id) => products.find((p) => p.id === id);
  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name.trim()) { toast.error("Enter the customer name."); return; }
    if (!f.line1.trim() || !f.city.trim()) { toast.error("Enter at least street and city."); return; }
    const clean = items.filter((it) => it.product_id);
    if (!clean.length) { toast.error("Add at least one product."); return; }
    for (const it of clean) {
      const p = prodOf(it.product_id);
      if (p?.sizes?.length && !it.size) { toast.error(`Select a size for ${p.name}.`); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/orders/manual", {
        customer_name: f.customer_name, customer_phone: f.customer_phone || null,
        customer_email: f.customer_email || null, order_type: f.order_type,
        shipping_address: { line1: f.line1, line2: f.line2, city: f.city, state: f.state,
          postal_code: f.postal_code, country: f.country },
        items: clean.map((it) => ({ product_id: it.product_id, color: it.color || null,
          size: it.size || null, quantity: parseInt(it.quantity, 10) || 1 })),
      });
      toast.success(`Order ${data.order_number} created (${f.order_type === "gift" ? "Gift" : "Manual"}).`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the order.");
    }
    setSaving(false);
  };

  return (
    <Modal open onClose={onClose} wide>
      <form onSubmit={submit} data-testid="manual-order-form">
        <p className="font-anton uppercase text-2xl mb-4">Create New Order</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><label className={labelCls}>Customer / influencer name *</label>
            <input value={f.customer_name} onChange={set("customer_name")} className={inputCls} data-testid="mo-name" /></div>
          <div><label className={labelCls}>Phone</label>
            <input value={f.customer_phone} onChange={set("customer_phone")} className={inputCls} data-testid="mo-phone" /></div>
          <div><label className={labelCls}>Email (for tracking)</label>
            <input type="email" value={f.customer_email} onChange={set("customer_email")} className={inputCls} data-testid="mo-email" /></div>
          <div><label className={labelCls}>Type</label>
            <select value={f.order_type} onChange={set("order_type")} className={`${inputCls} bg-ink`} data-testid="mo-type">
              <option value="gift">Gift (Presente)</option>
              <option value="manual">Manual</option>
            </select></div>
          <div className="sm:col-span-2"><label className={labelCls}>Street address *</label>
            <input value={f.line1} onChange={set("line1")} className={inputCls} data-testid="mo-line1" /></div>
          <div><label className={labelCls}>Apt / unit</label>
            <input value={f.line2} onChange={set("line2")} className={inputCls} data-testid="mo-line2" /></div>
          <div><label className={labelCls}>City *</label>
            <input value={f.city} onChange={set("city")} className={inputCls} data-testid="mo-city" /></div>
          <div><label className={labelCls}>State</label>
            <input value={f.state} onChange={set("state")} className={inputCls} data-testid="mo-state" /></div>
          <div><label className={labelCls}>ZIP / postal code</label>
            <input value={f.postal_code} onChange={set("postal_code")} className={inputCls} data-testid="mo-postal" /></div>
          <div><label className={labelCls}>Country</label>
            <input value={f.country} onChange={set("country")} className={inputCls} data-testid="mo-country" /></div>
        </div>

        <p className={`${labelCls} mt-5 mb-2`}>Products</p>
        {items.map((it, idx) => {
          const p = prodOf(it.product_id);
          return (
            <div key={idx} className="flex flex-wrap gap-2 mb-2 items-center" data-testid={`mo-item-${idx}`}>
              <select value={it.product_id} data-testid={`mo-item-product-${idx}`}
                onChange={(e) => setItem(idx, { product_id: e.target.value, color: prodOf(e.target.value)?.colors?.[0]?.name || "", size: "" })}
                className={`${inputCls} bg-ink flex-1 min-w-[180px]`}>
                <option value="">— Select product —</option>
                {products.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
              </select>
              <select value={it.color} onChange={(e) => setItem(idx, { color: e.target.value })}
                className={`${inputCls} bg-ink w-36`} disabled={!p} data-testid={`mo-item-color-${idx}`}>
                {(p?.colors || []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <select value={it.size} onChange={(e) => setItem(idx, { size: e.target.value })}
                className={`${inputCls} bg-ink w-24`} disabled={!p} data-testid={`mo-item-size-${idx}`}>
                <option value="">Size</option>
                {(p?.sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" min="1" max="50" value={it.quantity} data-testid={`mo-item-qty-${idx}`}
                onChange={(e) => setItem(idx, { quantity: e.target.value })} className={`${inputCls} w-20`} />
              {items.length > 1 && (
                <button type="button" onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}
                  className="font-mono text-xs text-pulse hover:text-destructive px-2" aria-label="Remove item">✕</button>
              )}
            </div>
          );
        })}
        <button type="button" onClick={() => setItems((arr) => [...arr, { product_id: "", color: "", size: "", quantity: 1 }])}
          className={`${btnGhost} mt-1`} data-testid="mo-add-item">+ Add Product</button>

        <p className="font-mono text-[10px] text-graphite mt-4">
          Saved with $0.00 total and a {f.order_type === "gift" ? "GIFT" : "MANUAL"} tag — it will NOT count toward sales or revenue metrics. Tracked stock is reduced normally.
        </p>
        <div className="flex gap-3 mt-4">
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="mo-save">
            {saving ? "Creating…" : "Create Order"}
          </button>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (tab === "all" ? orders : orders.filter((o) => (o.fulfillment_status || "new") === tab)),
    [orders, tab]);
  const counts = useMemo(() => TABS.reduce((acc, t) => ({
    ...acc, [t]: t === "all" ? orders.length : orders.filter((o) => (o.fulfillment_status || "new") === t).length,
  }), {}), [orders]);

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitle title="Orders" sub="Every paid order, from checkout to doorstep." />
        <button onClick={() => setCreating(true)} className={btnPrimary} data-testid="create-order-btn">
          + Create New Order
        </button>
      </div>
      <div className="flex flex-wrap gap-1 mb-5">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`orders-tab-${t}`}
            className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border ${
              tab === t ? "bg-chalk text-ink border-chalk" : "border-border text-graphite hover:border-chalk"}`}>
            {t === "all" ? "All" : STATUS_LABELS[t]} ({counts[t]})
          </button>
        ))}
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-border">
            <tr><Th>Order</Th><Th>Date</Th><Th>Customer</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><Td colSpan={6} className="text-graphite font-mono text-xs py-8 text-center">No orders here yet.</Td></tr>
            )}
            {filtered.map((o) => (
              <tr key={o.session_id} onClick={() => setSelected(o)} data-testid={`order-row-${o.session_id.slice(-8)}`}
                className="border-b border-border/40 hover:bg-chalk/5 cursor-pointer transition-colors">
                <Td className="font-mono text-xs text-signal">
                  {o.order_number || `…${o.session_id.slice(-8)}`}
                  {o.order_type && (
                    <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] border border-amber-500/50 text-amber-400 px-1.5 py-0.5"
                      data-testid={`order-type-badge-${o.session_id.slice(-8)}`}>
                      {o.order_type === "gift" ? "Gift" : "Manual"}
                    </span>
                  )}
                </Td>
                <Td className="font-mono text-xs text-graphite whitespace-nowrap">{fmtDateTime(o.created_at)}</Td>
                <Td>
                  <p className="text-sm">{o.customer_name || "—"}</p>
                  <p className="font-mono text-[10px] text-graphite">{o.customer_email || "—"}</p>
                </Td>
                <Td className="font-mono text-xs">{(o.items || []).reduce((n, i) => n + i.quantity, 0)}</Td>
                <Td className="font-mono text-xs">{fmtMoney(o.amount_total ?? o.amount)}</Td>
                <Td><StatusBadge status={o.fulfillment_status || "new"} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {selected && (
        <OrderDetail order={selected} onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); load(); }} />
      )}
      {creating && (
        <ManualOrderForm onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}
